import { useMemo, useState, type FormEvent } from 'react';
import { AlertTriangle, Plus, RefreshCw, Trash2 } from 'lucide-react';
import {
  useCommandes, useCreateCommande, useDeleteCommande, useUpdateCommande,
} from '@/hooks/useBetonCommandes';
import { useContacts } from '@/hooks/useCrm';
import { useChantiers, useBetonProduits } from '@/hooks/useBetonCatalog';
import { Dialog } from '@/components/ui/Dialog';
import { useAuthStore } from '@/stores/authStore';
import { extractError } from '@/api/client';
import {
  STATUT_COMMANDE_COLORS, STATUT_COMMANDE_LABELS,
  type Commande, type StatutCommande,
} from '@/types';
import { can } from '@/utils/permissions';
import { demander } from '@/components/ui/Fenetres';

const fmtInt = (n: number) =>
  new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 })
    .format(n)
    .replace(/[  ]/g, ' ');

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });

const todayIso = () => new Date().toISOString().slice(0, 10);

/** Colonnes kanban brief §2 — workflow Devis → Confirmée → ... → Facturée. */
const KANBAN_COLS: StatutCommande[] = [
  'devis', 'confirmee', 'en_fabrication', 'prete_au_parc',
  'expediee', 'livree', 'facturee',
];

const ALLOWED_TRANSITIONS: Record<StatutCommande, StatutCommande[]> = {
  devis:          ['confirmee', 'annulee'],
  confirmee:      ['en_fabrication', 'annulee'],
  en_fabrication: ['prete_au_parc', 'annulee'],
  prete_au_parc:  ['expediee', 'annulee'],
  expediee:       ['livree'],
  livree:         ['facturee'],
  facturee:       [],
  annulee:        [],
};

export const CommandesPage = () => {
  const role = useAuthStore((s) => s.user?.role);
  const canManage = can(role, 'operations.manage') || role === 'commercial';

  const [view, setView]               = useState<'kanban' | 'list'>('kanban');
  const [statutFilter, setStatutFilter] = useState<StatutCommande | ''>('');
  const [search, setSearch]           = useState('');
  const [dialogOpen, setDialogOpen]   = useState(false);
  const [editCommande, setEditCommande] = useState<Commande | undefined>(undefined);

  const { data, isLoading, refetch } = useCommandes({
    statut: statutFilter || undefined,
    search: search       || undefined,
    limit:  500,
  });
  const commandes = data?.data ?? [];

  // Groupé par statut pour kanban
  const byStatut = useMemo(() => {
    const m: Record<StatutCommande, Commande[]> = {
      devis: [], confirmee: [], en_fabrication: [], prete_au_parc: [],
      expediee: [], livree: [], facturee: [], annulee: [],
    };
    for (const c of commandes) (m[c.statut] ??= []).push(c);
    return m;
  }, [commandes]);

  return (
    <div className="ds-content">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
        <div style={{ flex: 1 }}>
          <h1 style={{ fontFamily: 'var(--font-t)', fontSize: 22, fontWeight: 700, color: 'var(--p-deep)', margin: 0 }}>
            Commandes
          </h1>
          <div style={{ fontSize: 12, color: 'var(--tx-3)' }}>
            {commandes.length} commande{commandes.length > 1 ? 's' : ''} · brief §2
          </div>
        </div>
        <div style={{ display: 'flex', gap: 4, border: '1px solid var(--bdr)', borderRadius: 'var(--r-sm)' }}>
          {([['kanban', 'Kanban'], ['list', 'Liste']] as const).map(([v, label]) => (
            <button key={v} type="button" onClick={() => setView(v)} style={{
              padding: '6px 12px', background: view === v ? 'var(--p)' : 'transparent',
              color: view === v ? 'white' : 'var(--tx-2)', border: 'none', cursor: 'pointer',
              fontSize: 12, fontWeight: 600,
            }}>{label}</button>
          ))}
        </div>
        <button type="button" onClick={() => refetch()} className="ds-btn ds-btn-g" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <RefreshCw size={14} /> Actualiser
        </button>
        {canManage && (
          <button type="button" onClick={() => { setEditCommande(undefined); setDialogOpen(true); }} className="ds-btn ds-btn-p" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Plus size={14} /> Nouvelle commande
          </button>
        )}
      </div>

      {/* Filtres en mode liste */}
      {view === 'list' && (
        <div className="ds-bp" style={{ marginBottom: 14 }}>
          <div className="ds-bp-b" style={{ padding: 12 }}>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Statut">
                <select value={statutFilter} onChange={(e) => setStatutFilter(e.target.value as StatutCommande | '')} className="ds-fi" style={{ width: '100%' }}>
                  <option value="">— Tous —</option>
                  {(Object.keys(STATUT_COMMANDE_LABELS) as StatutCommande[]).map((s) => (
                    <option key={s} value={s}>{STATUT_COMMANDE_LABELS[s]}</option>
                  ))}
                </select>
              </Field>
              <Field label="Recherche">
                <input value={search} onChange={(e) => setSearch(e.target.value)} className="ds-fi" style={{ width: '100%' }} placeholder="Référence / nom client" />
              </Field>
            </div>
          </div>
        </div>
      )}

      {isLoading ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--tx-3)' }}>Chargement…</div>
      ) : view === 'kanban' ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 8, overflowX: 'auto', minHeight: 400 }}>
          {KANBAN_COLS.map((statut) => (
            <KanbanColumn
              key={statut}
              statut={statut}
              commandes={byStatut[statut]}
              onClick={(c) => { setEditCommande(c); setDialogOpen(true); }}
            />
          ))}
        </div>
      ) : (
        <ListView commandes={commandes} onClick={(c) => { setEditCommande(c); setDialogOpen(true); }} />
      )}

      <CommandeDialog open={dialogOpen} onClose={() => setDialogOpen(false)} commande={editCommande} />
    </div>
  );
};

// ── Kanban column ──────────────────────────────────────────────────────────

const KanbanColumn = ({ statut, commandes, onClick }: { statut: StatutCommande; commandes: Commande[]; onClick: (c: Commande) => void }) => {
  const color = STATUT_COMMANDE_COLORS[statut];
  return (
    <div style={{ background: 'var(--surface)', borderRadius: 'var(--r-sm)', border: '1px solid var(--bdr)', minWidth: 180 }}>
      <div style={{ padding: 8, borderBottom: `3px solid ${color}`, background: 'white' }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--tx-2)', textTransform: 'uppercase' }}>
          {STATUT_COMMANDE_LABELS[statut]}
        </div>
        <div style={{ fontSize: 11, color: 'var(--tx-3)' }}>{commandes.length} commande{commandes.length > 1 ? 's' : ''}</div>
      </div>
      <div style={{ padding: 6, display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 600, overflowY: 'auto' }}>
        {commandes.map((c) => (
          <div key={c.id} onClick={() => onClick(c)} style={{
            background: 'white', padding: 8, borderRadius: 4, border: '1px solid var(--bdr)',
            cursor: 'pointer', fontSize: 11,
          }}>
            <div style={{ fontWeight: 700, color: 'var(--p)' }}>{c.reference}</div>
            <div style={{ color: 'var(--tx-2)', fontSize: 11, marginTop: 2 }}>{c.client?.companyName ?? c.client?.fullName ?? '—'}</div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4, fontSize: 10, color: 'var(--tx-3)' }}>
              <span>{fmtDate(c.dateCommande)}</span>
              <span style={{ fontWeight: 700, color: 'var(--p)' }}>{fmtInt(parseFloat(c.montantHtFcfa))} F</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── Liste ──────────────────────────────────────────────────────────────────

const ListView = ({ commandes, onClick }: { commandes: Commande[]; onClick: (c: Commande) => void }) => (
  <div className="ds-bp">
    <div className="ds-bp-b">
      {commandes.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--tx-3)' }}>Aucune commande</div>
      ) : (
        <table className="ds-tbl">
          <thead>
            <tr>
              <th>Référence</th>
              <th>Date</th>
              <th>Client</th>
              <th>Chantier</th>
              <th className="ds-am">Poids</th>
              <th className="ds-am">Montant HT</th>
              <th>Livraison</th>
              <th>Statut</th>
            </tr>
          </thead>
          <tbody>
            {commandes.map((c) => (
              <tr key={c.id} onClick={() => onClick(c)} style={{ cursor: 'pointer' }}>
                <td><span className="ds-ref-code">{c.reference}</span></td>
                <td style={{ fontSize: 11, fontFamily: 'var(--font-m)' }}>{fmtDate(c.dateCommande)}</td>
                <td style={{ fontWeight: 600 }}>{c.client?.companyName ?? c.client?.fullName ?? '—'}</td>
                <td style={{ fontSize: 12, color: 'var(--tx-3)' }}>{c.chantier?.code ?? '—'}</td>
                <td className="ds-am" style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtInt(parseFloat(c.poidsTotalKg))} kg</td>
                <td className="ds-am" style={{ fontWeight: 700, color: 'var(--p)', fontVariantNumeric: 'tabular-nums' }}>{fmtInt(parseFloat(c.montantHtFcfa))} F</td>
                <td style={{ fontSize: 11 }}>{c.dateLivraisonSouhaitee ? fmtDate(c.dateLivraisonSouhaitee) : '—'}</td>
                <td>
                  <span style={{
                    display: 'inline-block', padding: '2px 8px', borderRadius: 10,
                    fontSize: 10, fontWeight: 700, color: 'white',
                    background: STATUT_COMMANDE_COLORS[c.statut],
                  }}>{STATUT_COMMANDE_LABELS[c.statut]}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  </div>
);

// ── Dialog création/édition commande ───────────────────────────────────────

interface LigneState {
  produitId:        string;
  quantite:         string;
  prixUnitaireFcfa: string;
  remiseLignePct:   string;
}

const CommandeDialog = ({ open, onClose, commande }: { open: boolean; onClose: () => void; commande?: Commande }) => {
  const isEdit = !!commande;
  const create = useCreateCommande();
  const update = useUpdateCommande();
  const remove = useDeleteCommande();
  const { data: contactsData }  = useContacts({ contactType: 'client', limit: 500 });
  const { data: chantiersData } = useChantiers({ limit: 200 });
  const { data: produitsData }  = useBetonProduits({ actif: true, limit: 500 });
  const contacts  = contactsData?.data  ?? [];
  const chantiers = chantiersData?.data ?? [];
  const produits  = produitsData?.data  ?? [];

  const [clientId, setClientId]                       = useState('');
  const [chantierId, setChantierId]                   = useState('');
  const [dateCommande, setDateCommande]               = useState(todayIso());
  const [dateLivraisonSouhaitee, setDateLivraisonSouhaitee] = useState('');
  const [remisePct, setRemisePct]                     = useState('0');
  const [statut, setStatut]                           = useState<StatutCommande>('devis');
  const [raisonAnnulation, setRaisonAnnulation]       = useState('');
  const [lignes, setLignes]                           = useState<LigneState[]>([]);
  const [notes, setNotes]                             = useState('');
  const [error, setError]                             = useState<string | null>(null);

  // Reset à chaque ouverture
  useMemo(() => {
    if (!open) return;
    if (commande) {
      setClientId(commande.clientId);
      setChantierId(commande.chantierId ?? '');
      setDateCommande(commande.dateCommande.slice(0, 10));
      setDateLivraisonSouhaitee(commande.dateLivraisonSouhaitee?.slice(0, 10) ?? '');
      setRemisePct(commande.remisePct);
      setStatut(commande.statut);
      setRaisonAnnulation(commande.raisonAnnulation ?? '');
      setLignes((commande.lignes ?? []).map((l) => ({
        produitId:        l.produitId,
        quantite:         l.quantite,
        prixUnitaireFcfa: l.prixUnitaireFcfa,
        remiseLignePct:   l.remiseLignePct,
      })));
      setNotes(commande.notes ?? '');
    } else {
      setClientId(''); setChantierId('');
      setDateCommande(todayIso()); setDateLivraisonSouhaitee('');
      setRemisePct('0'); setStatut('devis'); setRaisonAnnulation('');
      setLignes([]); setNotes('');
    }
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, commande]);

  // Auto-fill prix unitaire au changement de produit
  const onProduitChange = (i: number, newProduitId: string) => {
    const produit = produits.find((p) => p.id === newProduitId);
    setLignes((ls) => ls.map((l, idx) => idx === i ? {
      ...l,
      produitId: newProduitId,
      prixUnitaireFcfa: l.prixUnitaireFcfa === '0' || !l.prixUnitaireFcfa
        ? String(Number(produit?.prixVenteCents ?? 0) / 100)
        : l.prixUnitaireFcfa,
    } : l));
  };

  // Calculs live
  const totaux = useMemo(() => {
    let totalLignes = 0;
    let poidsKg     = 0;
    let maxCure     = 0;
    for (const l of lignes) {
      const q = parseFloat(l.quantite)         || 0;
      const p = parseFloat(l.prixUnitaireFcfa) || 0;
      const r = parseFloat(l.remiseLignePct)   || 0;
      const sub = q * p * (1 - r / 100);
      totalLignes += sub;
      const produit = produits.find((pp) => pp.id === l.produitId);
      poidsKg += q * Number(produit?.poidsKg ?? 0);
      maxCure = Math.max(maxCure, produit?.delaiCureJours ?? 0);
    }
    const rem      = parseFloat(remisePct) || 0;
    const montantHt = totalLignes * (1 - rem / 100);
    return { totalLignes, montantHt, poidsKg, maxCure };
  }, [lignes, remisePct, produits]);

  const warnDelai = useMemo(() => {
    if (!dateLivraisonSouhaitee || totaux.maxCure === 0) return null;
    const d = new Date(dateLivraisonSouhaitee).getTime();
    const t = new Date(dateCommande).getTime();
    const diff = Math.ceil((d - t) / (24 * 3600_000));
    if (diff < totaux.maxCure) {
      return `⚠️ Délai livraison ${diff}j < délai cure max ${totaux.maxCure}j d'un produit commandé.`;
    }
    return null;
  }, [dateLivraisonSouhaitee, dateCommande, totaux.maxCure]);

  const addLigne = () => setLignes([...lignes, { produitId: '', quantite: '1', prixUnitaireFcfa: '0', remiseLignePct: '0' }]);
  const removeLigne = (i: number) => setLignes(lignes.filter((_, idx) => idx !== i));
  const updateLigne = (i: number, patch: Partial<LigneState>) => setLignes(lignes.map((l, idx) => idx === i ? { ...l, ...patch } : l));

  const allowedNext = isEdit ? ALLOWED_TRANSITIONS[commande!.statut] : [];

  const canSubmit = clientId && dateCommande && lignes.length > 0 && lignes.every((l) => l.produitId && parseFloat(l.quantite) > 0);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const lignesPayload = lignes.map((l) => ({
      produitId:        l.produitId,
      quantite:         parseFloat(l.quantite),
      prixUnitaireFcfa: parseFloat(l.prixUnitaireFcfa),
      remiseLignePct:   parseFloat(l.remiseLignePct) || 0,
    }));
    try {
      if (isEdit) {
        await update.mutateAsync({
          id: commande!.id,
          payload: {
            clientId,
            chantierId:             chantierId || undefined,
            dateCommande,
            dateLivraisonSouhaitee: dateLivraisonSouhaitee || undefined,
            remisePct:              parseFloat(remisePct) || 0,
            statut:                 statut !== commande!.statut ? statut : undefined,
            raisonAnnulation:       statut === 'annulee' ? raisonAnnulation : undefined,
            lignes:                 commande!.statut === 'devis' ? lignesPayload : undefined, // édition lignes verrouillée hors devis
            notes:                  notes || undefined,
          },
        });
      } else {
        await create.mutateAsync({
          clientId,
          chantierId:             chantierId || undefined,
          dateCommande,
          dateLivraisonSouhaitee: dateLivraisonSouhaitee || undefined,
          remisePct:              parseFloat(remisePct) || 0,
          lignes:                 lignesPayload,
          notes:                  notes || undefined,
        });
      }
      onClose();
    } catch (err) {
      setError(extractError(err).message);
    }
  };

  const onDelete = async () => {
    if (!commande) return;
    if (!(await demander.confirmer({ titre: `Supprimer définitivement la commande ${commande.reference} ?`, confirmer: 'Supprimer', danger: true }))) return;
    try {
      await remove.mutateAsync(commande.id);
      onClose();
    } catch (err) {
      setError(extractError(err).message);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={isEdit ? `Commande ${commande!.reference}` : 'Nouvelle commande'}
      subtitle={isEdit ? `Statut : ${STATUT_COMMANDE_LABELS[commande!.statut]} · ${fmtInt(parseFloat(commande!.montantHtFcfa))} F HT` : 'Brief §2 · Statut initial = devis'}
      size="lg"
    >
      <form onSubmit={onSubmit} className="space-y-3">
        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
            <AlertTriangle className="inline size-3 mr-1" />{error}
          </div>
        )}
        {warnDelai && (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">{warnDelai}</div>
        )}

        <Section title="1 · Client & chantier">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Client *">
              <select value={clientId} onChange={(e) => setClientId(e.target.value)} required className="ds-fi" style={{ width: '100%' }}>
                <option value="">— Sélectionner —</option>
                {contacts.map((c) => <option key={c.id} value={c.id}>{c.companyName ? `${c.companyName} · ${c.fullName}` : c.fullName}</option>)}
              </select>
            </Field>
            <Field label="Chantier (optionnel)">
              <select value={chantierId} onChange={(e) => setChantierId(e.target.value)} className="ds-fi" style={{ width: '100%' }}>
                <option value="">— Aucun —</option>
                {chantiers.map((c) => <option key={c.id} value={c.id}>{c.code} · {c.nom}</option>)}
              </select>
            </Field>
            <Field label="Date commande *">
              <input type="date" value={dateCommande} onChange={(e) => setDateCommande(e.target.value)} required className="ds-fi" style={{ width: '100%' }} />
            </Field>
            <Field label="Livraison souhaitée">
              <input type="date" value={dateLivraisonSouhaitee} onChange={(e) => setDateLivraisonSouhaitee(e.target.value)} className="ds-fi" style={{ width: '100%' }} />
            </Field>
          </div>
        </Section>

        <Section title={`2 · Lignes${lignes.length > 0 ? ` (${lignes.length})` : ''}`}>
          {lignes.length === 0 ? (
            <div style={{ padding: 8, color: 'var(--tx-3)', fontSize: 12, fontStyle: 'italic' }}>
              Aucune ligne. Clique « Ajouter une ligne ».
            </div>
          ) : (
            <div style={{ display: 'grid', gap: 6 }}>
              {lignes.map((l, i) => {
                const produit = produits.find((p) => p.id === l.produitId);
                const subTotal = (parseFloat(l.quantite) || 0) * (parseFloat(l.prixUnitaireFcfa) || 0) * (1 - (parseFloat(l.remiseLignePct) || 0) / 100);
                return (
                  <div key={i} style={{ display: 'grid', gridTemplateColumns: '3fr 0.8fr 1.2fr 0.8fr 1.2fr auto', gap: 6, alignItems: 'center' }}>
                    <select value={l.produitId} onChange={(e) => onProduitChange(i, e.target.value)} className="ds-fi" style={{ fontSize: 11 }}>
                      <option value="">— Produit —</option>
                      {produits.map((p) => <option key={p.id} value={p.id}>{p.code} · {p.designation}</option>)}
                    </select>
                    <input type="number" min={0} step="0.1" value={l.quantite} onChange={(e) => updateLigne(i, { quantite: e.target.value })} className="ds-fi" placeholder="Qté" />
                    <input type="number" min={0} value={l.prixUnitaireFcfa} onChange={(e) => updateLigne(i, { prixUnitaireFcfa: e.target.value })} className="ds-fi" placeholder="Prix unit" />
                    <input type="number" min={0} max={100} value={l.remiseLignePct} onChange={(e) => updateLigne(i, { remiseLignePct: e.target.value })} className="ds-fi" placeholder="Remise %" />
                    <div style={{ fontSize: 11, color: 'var(--p)', fontWeight: 700, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      {fmtInt(subTotal)} F
                      {produit?.delaiCureJours ? <div style={{ fontSize: 9, color: 'var(--tx-3)' }}>Cure {produit.delaiCureJours}j</div> : null}
                    </div>
                    <button type="button" onClick={() => removeLigne(i)} disabled={isEdit && commande!.statut !== 'devis'} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#dc2626' }}><Trash2 size={14} /></button>
                  </div>
                );
              })}
            </div>
          )}
          {(!isEdit || commande!.statut === 'devis') && (
            <button type="button" onClick={addLigne} className="ds-btn ds-btn-g" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Plus size={12} /> Ajouter une ligne
            </button>
          )}
        </Section>

        <Section title="3 · Synthèse">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, padding: 8, background: 'var(--surface)', borderRadius: 4 }}>
            <Stat label="Total lignes" value={`${fmtInt(totaux.totalLignes)} F`} />
            <Field label="Remise globale (%)">
              <input type="number" min={0} max={100} value={remisePct} onChange={(e) => setRemisePct(e.target.value)} className="ds-fi" style={{ width: '100%' }} />
            </Field>
            <Stat label="Montant HT"  value={`${fmtInt(totaux.montantHt)} F`}    strong color="var(--p)" />
            <Stat label="Poids total" value={`${fmtInt(totaux.poidsKg)} kg`} />
          </div>
        </Section>

        {isEdit && (
          <Section title="4 · Workflow statut">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Statut">
                <select value={statut} onChange={(e) => setStatut(e.target.value as StatutCommande)} className="ds-fi" style={{ width: '100%' }} disabled={allowedNext.length === 0 && statut === commande!.statut}>
                  <option value={commande!.statut}>{STATUT_COMMANDE_LABELS[commande!.statut]} (actuel)</option>
                  {allowedNext.map((s) => <option key={s} value={s}>→ {STATUT_COMMANDE_LABELS[s]}</option>)}
                </select>
              </Field>
              {statut === 'annulee' && (
                <Field label="Raison annulation *">
                  <input value={raisonAnnulation} onChange={(e) => setRaisonAnnulation(e.target.value)} required className="ds-fi" style={{ width: '100%' }} />
                </Field>
              )}
            </div>
            {allowedNext.length === 0 && commande!.statut !== 'devis' && (
              <div style={{ fontSize: 11, color: 'var(--tx-3)', fontStyle: 'italic' }}>
                Statut terminal — aucune transition possible.
              </div>
            )}
          </Section>
        )}

        <Field label="Notes">
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="ds-fi" style={{ width: '100%' }} />
        </Field>

        <div className="flex justify-between border-t border-gray-100 pt-3">
          <div>
            {isEdit && (commande!.statut === 'devis' || commande!.statut === 'annulee') && (
              <button type="button" onClick={onDelete} className="ds-btn ds-btn-g" style={{ color: '#dc2626', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <Trash2 size={14} /> Supprimer
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <button type="button" className="ds-btn ds-btn-g" onClick={onClose}>Annuler</button>
            <button type="submit" className="ds-btn ds-btn-p" disabled={!canSubmit || create.isPending || update.isPending}>
              {create.isPending || update.isPending ? 'Enregistrement…' : isEdit ? 'Mettre à jour' : 'Créer la commande'}
            </button>
          </div>
        </div>
      </form>
    </Dialog>
  );
};

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <fieldset style={{ border: '1px solid var(--bdr)', borderRadius: 'var(--r-sm)', padding: 10 }} className="space-y-2">
    <legend style={{ fontSize: 11, fontWeight: 700, color: 'var(--tx-2)', textTransform: 'uppercase', letterSpacing: 0.5, padding: '0 6px' }}>{title}</legend>
    {children}
  </fieldset>
);

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div><label className="mb-1 block text-xs font-medium text-gray-600">{label}</label>{children}</div>
);

const Stat = ({ label, value, color, strong }: { label: string; value: string; color?: string; strong?: boolean }) => (
  <div>
    <div style={{ fontSize: 10, color: 'var(--tx-3)', textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
    <div style={{ fontSize: strong ? 16 : 13, fontWeight: strong ? 700 : 500, color: color ?? 'var(--tx-1)', fontVariantNumeric: 'tabular-nums', letterSpacing: 'normal' }}>{value}</div>
  </div>
);
